// step 1 of the sponsor pledge flow
import React from 'react'
import { Trans } from 'react-i18next'

import Icon from '../../../../components/icon'
import EzTooltip from '../../../../components/tooltip'
import { formatAmount } from '../../../../lib/numbers'

function UnderlinedTooltip({ id, tip, className, children }) {
  return (
    <EzTooltip
      id={id}
      type="primary"
      placement="top"
      outerContent={<span className={className}>{children}</span>}
      innerContent={tip}
    />
  )
}

export default function Step1Commitment({
  styles,
  t,
  minPledge,
  meetsMin,
  commitmentNum,
  commitmentInput,
  setCommitmentInput,
  spinCommitment,
  onEnterStep2,
}) {
  const minFormatted = formatAmount(minPledge, true)
  const tooltipTriggers = {
    eventMultiplier: (
      <UnderlinedTooltip
        id="sponsor-pledge-event-multiplier"
        tip={t('sponsorPledgeFlow.tier.tooltips.eventMultiplier')}
        className={styles.tooltipTrigger}
      />
    ),
    mpf: (
      <UnderlinedTooltip
        id="sponsor-pledge-mpf"
        tip={t('sponsorPledgeFlow.tier.tooltips.mpf')}
        className={styles.tooltipTrigger}
      />
    ),
    vpf: (
      <UnderlinedTooltip
        id="sponsor-pledge-vpf"
        tip={t('sponsorPledgeFlow.tier.tooltips.vpf')}
        className={styles.tooltipTrigger}
      />
    ),
  }

  return (
    <>
      <h1 className={styles.stepHeading}>
        {t('sponsorPledgeFlow.step1.heading')}
      </h1>
      <p className={styles.stepSubheading}>
        {t('sponsorPledgeFlow.step1.subheading')}
      </p>

      <div className={styles.tierCard}>
        <div className={styles.tierCardName}>
          {t('sponsorPledgeFlow.tier.name')}
          <span className={styles.exclBadge}>
            {t('sponsorPledgeFlow.tier.badge')}
          </span>
        </div>
        <p className={styles.tierCardDesc}>
          <Trans
            i18nKey="sponsorPledgeFlow.tier.description"
            components={tooltipTriggers}
          />
        </p>
        <div className={styles.tierPerks}>
          <div className={styles.tierPerk}>
            <Icon name="check" className={styles.tierPerkIcon} />
            <span>{t('sponsorPledgeFlow.tier.perk1')}</span>
          </div>
          <div className={styles.tierPerk}>
            <Icon name="check" className={styles.tierPerkIcon} />
            <span>{t('sponsorPledgeFlow.tier.perk2')}</span>
          </div>
          <div className={styles.tierPerk}>
            <Icon name="check" className={styles.tierPerkIcon} />
            <span>
              <Trans
                i18nKey="sponsorPledgeFlow.tier.perk3"
                components={tooltipTriggers}
              />
            </span>
          </div>
          <div className={styles.tierPerk}>
            <Icon name="check" className={styles.tierPerkIcon} />
            <span>
              <Trans
                i18nKey="sponsorPledgeFlow.tier.perk4"
                components={tooltipTriggers}
              />
            </span>
          </div>
        </div>
        <p className={styles.tierFeeNote}>
          {t('sponsorPledgeFlow.tier.feeNote', { min: minFormatted })}
        </p>
      </div>

      <div className={styles.sectionLabel} style={{ marginTop: 0 }}>
        {t('sponsorPledgeFlow.step1.pledgeLabel')}
        <span className={styles.crRequired}>*</span>
      </div>
      <p className={styles.stepSubheading} style={{ marginTop: 0 }}>
        {t('sponsorPledgeFlow.step1.pledgeHint', { min: minFormatted })}
      </p>

      <div
        className={[
          styles.amountWrap,
          meetsMin ? styles.amountWrapValid : '',
          commitmentNum > 0 && !meetsMin ? styles.amountWrapInvalid : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <span className={styles.amountCurrency}>$</span>
        <input
          className={styles.amountInput}
          type="text"
          inputMode="numeric"
          value={commitmentInput}
          onChange={e =>
            setCommitmentInput(e.target.value.replace(/[^\d]/g, ''))
          }
          placeholder={String(minPledge || 1000)}
          aria-label={t('sponsorPledgeFlow.step1.pledgeLabel')}
        />
        <div className={styles.amountSpinners}>
          <button
            type="button"
            className={styles.amountSpinnerBtn}
            onClick={() => spinCommitment(50)}
          >
            <Icon name="chevron-up" />
          </button>
          <button
            type="button"
            className={styles.amountSpinnerBtn}
            onClick={() => spinCommitment(-50)}
          >
            <Icon name="chevron-down" />
          </button>
        </div>
      </div>
      {commitmentNum > 0 && !meetsMin ? (
        <div className={styles.errorMsg}>
          {t('sponsorPledgeFlow.step1.minError', {
            min: minFormatted,
          })}
        </div>
      ) : null}

      <div className={styles.infoNote}>
        <Icon name="info-circle" />
        <span>{t('sponsorPledgeFlow.step1.infoNote')}</span>
      </div>

      <div className={styles.stepNav}>
        <div />
        <button
          type="button"
          className={styles.btnPrimary}
          disabled={!meetsMin}
          onClick={onEnterStep2}
        >
          {t('sponsorPledgeFlow.step1.cta')}
          <Icon name="chevron-right" />
        </button>
      </div>
    </>
  )
}
